import { AdminRole } from "@repo/api-dtos"
import {
	CreditCard,
	FileSpreadsheet,
	Gauge,
	Handshake,
	ShieldAlert,
	ShoppingBag,
	Star,
	Ticket,
	Users,
} from "lucide-react"
import {
	API_USAGE_SEARCH_DEFAULTS,
	COUPONS_SEARCH_DEFAULTS,
	MODERATION_SEARCH_DEFAULTS,
	MONTHLY_CLOSES_SEARCH_DEFAULTS,
	OFFERS_SEARCH_DEFAULTS,
	ORDERS_SEARCH_DEFAULTS,
	REVIEW_APPEALS_SEARCH_DEFAULTS,
	TRANSACTIONS_SEARCH_DEFAULTS,
	USERS_SEARCH_DEFAULTS,
} from "@/config/search-defaults"
import type { PaletteCommand, PaletteContext } from "../types"

const MARKETPLACE = [
	AdminRole.MarketplaceRead,
	AdminRole.MarketplaceOrderSupport,
	AdminRole.MarketplaceUserManagement,
	AdminRole.MarketplaceCoupons,
]

function go(perform: (ctx: PaletteContext) => void) {
	return (ctx: PaletteContext) => {
		perform(ctx)
		ctx.close()
	}
}

export function buildPresetCommands(): PaletteCommand[] {
	const orders = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:orders:${id}`,
		label: `Orders › ${label}`,
		icon: ShoppingBag,
		group: "navigate",
		requiredRoles: MARKETPLACE,
		perform: go((ctx) => {
			ctx.navigate({
				to: "/orders",
				search: { ...ORDERS_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const offers = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:offers:${id}`,
		label: `Offers › ${label}`,
		icon: Handshake,
		group: "navigate",
		requiredRoles: MARKETPLACE,
		perform: go((ctx) => {
			ctx.navigate({
				to: "/offers",
				search: { ...OFFERS_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const transactions = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:transactions:${id}`,
		label: `Transactions › ${label}`,
		icon: CreditCard,
		group: "navigate",
		requiredRoles: MARKETPLACE,
		perform: go((ctx) => {
			ctx.navigate({
				to: "/transactions",
				search: { ...TRANSACTIONS_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const users = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:users:${id}`,
		label: `Users › ${label}`,
		icon: Users,
		group: "navigate",
		requiredRoles: MARKETPLACE,
		perform: go((ctx) => {
			ctx.navigate({
				to: "/users",
				search: { ...USERS_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const coupons = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:coupons:${id}`,
		label: `Coupons › ${label}`,
		icon: Ticket,
		group: "navigate",
		requiredRoles: [AdminRole.MarketplaceRead, AdminRole.MarketplaceCoupons],
		perform: go((ctx) => {
			ctx.navigate({
				to: "/coupons",
				search: { ...COUPONS_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const moderation = (
		id: string,
		label: string,
		search: Record<string, unknown>,
	): PaletteCommand => ({
		id: `preset:moderation:${id}`,
		label: `Moderation › ${label}`,
		icon: ShieldAlert,
		group: "navigate",
		requiredRoles: [AdminRole.MarketplaceUserManagement],
		perform: go((ctx) => {
			ctx.navigate({
				to: "/moderation",
				search: { ...MODERATION_SEARCH_DEFAULTS, ...search },
			})
		}),
	})

	const currentYear = new Date().getFullYear()

	return [
		orders("escalated", "Escalated disputes", { dispute: "escalated" }),
		orders("needs-action", "Needs action", { needsAction: "true" }),
		orders("escalated-needs-action", "Escalated and needs action", {
			dispute: "escalated",
			needsAction: "true",
			sortBy: "needsActionChangedAt",
			sortOrder: "asc",
		}),
		orders("in-transit", "In transit", { status: "shipped" }),
		orders("pending-shipment", "Pending shipment", {
			status: "pending_shipment",
		}),
		orders("needs-review", "Needs risk review", {
			reviewStatus: "needs_review",
		}),
		orders("stalled", "Stalled shipments (shipped > 14 d)", {
			status: "shipped",
			minDaysInStatus: 14,
			sortBy: "statusChangedAt",
			sortOrder: "asc",
		}),
		orders("delivered-7d", "Delivered > 7 d", {
			status: "delivered",
			minDaysInStatus: 7,
		}),

		offers("waiting-seller", "Waiting on seller", {
			status: "pending,countered",
			awaiting: "seller",
		}),
		offers("waiting-buyer", "Waiting on buyer", {
			status: "pending,countered",
			awaiting: "buyer",
		}),
		offers("accepted", "Accepted, awaiting checkout", {
			status: "accepted",
			redeemed: "false",
		}),
		offers("redeemed", "Redeemed", { redeemed: "true" }),
		offers("voided", "Voided", { status: "voided" }),
		offers("idle", "Idle > 2 d", {
			status: "pending,countered",
			minDaysWaiting: 2,
		}),

		transactions("failed", "Failed", { status: "failed" }),
		transactions("critical-risk", "Critical risk", { riskLevel: "critical" }),
		transactions("refunded", "Refunded", { status: "refunded" }),
		transactions("disputed", "With dispute", { dispute: "has" }),

		users("sellers", "Sellers", { isSeller: "yes" }),
		users("suspended", "Suspended", { accountStatus: "suspended" }),
		users("banned", "Banned", { accountStatus: "banned" }),
		users("flagged", "Flagged", { accountStatus: "flagged" }),

		coupons("active", "Active", { status: "active" }),
		coupons("inactive", "Inactive", { status: "inactive" }),
		coupons("percentage", "Percentage", { type: "percentage" }),
		coupons("fixed", "Fixed amount", { type: "fixed_amount" }),

		moderation("pending", "Needs confirmation", { pending: "1" }),
		moderation("severe", "Critical and high", { severity: "critical,high" }),
		moderation("open", "Open", { status: "open" }),
		moderation("reviewing", "Reviewing", { status: "reviewing" }),
		moderation("resolved", "Resolved", { status: "resolved" }),
		moderation("all", "All", { status: "all" }),

		{
			id: "preset:review-appeals:pending",
			label: "Review appeals › Pending",
			icon: Star,
			group: "navigate",
			requiredRoles: MARKETPLACE,
			perform: go((ctx) => {
				ctx.navigate({
					to: "/review-appeals",
					search: { ...REVIEW_APPEALS_SEARCH_DEFAULTS, status: "pending" },
				})
			}),
		},

		...(
			[
				["drafts", "Drafts", { status: "draft" }],
				["na", "NA", { region: "NA" }],
				["eu", "EU", { region: "EU" }],
				["year", "Current year", { year: currentYear }],
			] as const
		).map(
			([id, label, search]): PaletteCommand => ({
				id: `preset:monthly-closes:${id}`,
				label: `Monthly closes › ${label}`,
				icon: FileSpreadsheet,
				group: "navigate",
				requiredRoles: [AdminRole.MarketplaceFinance],
				perform: go((ctx) => {
					ctx.navigate({
						to: "/accounting/monthly-closes",
						search: { ...MONTHLY_CLOSES_SEARCH_DEFAULTS, ...search },
					})
				}),
			}),
		),

		...(
			[
				["24h", "Last 24 h", { window: "24h" }],
				["7d", "Last 7 d", { window: "7d" }],
				["30d", "Last 30 d", { window: "30d" }],
				["by-app", "Grouped by app", { group: "app" }],
				["by-account", "Grouped by account", { group: "account" }],
			] as const
		).map(
			([id, label, search]): PaletteCommand => ({
				id: `preset:api-usage:${id}`,
				label: `API usage › ${label}`,
				icon: Gauge,
				group: "navigate",
				requiredRoles: [AdminRole.IntegrationsAdmin],
				perform: go((ctx) => {
					ctx.navigate({
						to: "/api-usage",
						search: { ...API_USAGE_SEARCH_DEFAULTS, ...search },
					})
				}),
			}),
		),
	]
}
